// The orchestrator: an input plus a recipe becomes a finished page.
//
// ORDER IS LOAD-BEARING, and one step is commonly got wrong. The content is
// resampled to the fax grid BEFORE it is dithered, never after. Dithering and
// then resizing reintroduces grey — the resampler averages neighbouring dots
// back into midtones — and the result looks like a photograph of a fax rather
// than a fax.
//
// The other rule: the page is composed with real text, and canvas fillText is
// ANTIALIASED. So the very last thing that happens to the whole page is a hard
// threshold, which is a no-op on already-1-bit pixels and costs a few
// milliseconds. It is what guarantees the invariant this app rests on: every
// exported pixel is exactly #000000 or #FFFFFF.

import {
  GRID_W, GRID_H, OUT_W, OUT_H,
  HEADER_H, FOOTER_H, MARGIN_X, MARGIN_Y, HEADER_TOP, CONTENT_TOP, CONTENT_H,
  PAPER, INK, MID, WRECK, V_SCALE,
} from './geometry.js';
import { buildLut, toGrey, unsharp } from './tone.js';
import { algorithmFor, toWorkBuffer, toBytes } from './dither.js';
import { mulberry32, tonerBanding, applyPost } from './artifacts.js';

const two = (n) => String(n).padStart(2, '0');

/** The line every real fax page carries. Also where the reply URL lives. */
export function headerLine(fax, machine, now = new Date()) {
  const d = machine?.dateFormat === 'mdy'
    ? `${two(now.getMonth() + 1)}/${two(now.getDate())}`
    : `${two(now.getDate())}/${two(now.getMonth() + 1)}`;
  const time = `${two(now.getHours())}:${two(now.getMinutes())}`;
  const from = (machine?.tsi || 'UNNAMED MACHINE').toUpperCase().slice(0, 20);
  const num = machine?.number || '';
  const page = `P.${String(fax?.pages || 1).padStart(3, '0')}/${String(fax?.pages || 1).padStart(3, '0')}`;
  return { left: `${d} ${time}`, mid: `FROM: ${from}`, num, page };
}

function ctx2d(w, h) {
  const c = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(w, h)
    : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const g = c.getContext('2d', { willReadFrequently: true });
  return { canvas: c, g };
}

/**
 * Decode at a reduced size.
 *
 * NEVER decode a full-resolution photo. A 12 MP HEIC is ~48 MB as RGBA, and
 * iOS Safari does not throw when canvas memory runs out — it hands back a
 * BLANK canvas, so the failure is a silently empty fax rather than an error.
 */
export async function decodeSmall(blobOrUrl, width = GRID_W) {
  if (typeof createImageBitmap === 'function' && blobOrUrl instanceof Blob) {
    return createImageBitmap(blobOrUrl, { resizeWidth: width, resizeQuality: 'high' });
  }
  const img = new Image();
  img.decoding = 'async';
  img.src = typeof blobOrUrl === 'string' ? blobOrUrl : URL.createObjectURL(blobOrUrl);
  await img.decode();
  return img;
}


/** How much of the page the cover band needs, or 0 when there is nothing to say. */
function coverBandHeight(fax) {
  const has = fax?.toName || fax?.subject || fax?.memo;
  if (!has) return 0;
  return 44 + (fax?.subject ? 30 : 0) + (fax?.memo ? 34 : 0);
}

/**
 * The cover information printed above a photo, the way a fax machine does it:
 * one dense mono block and a rule, then the picture underneath.
 */
function drawCoverBand(g, fax, w, h) {
  g.fillStyle = '#000000';
  g.textBaseline = 'top';
  let y = 8;

  g.font = '700 24px ui-monospace, "SF Mono", Menlo, Consolas, monospace';
  const to = String(fax?.toName || '').toUpperCase().slice(0, 24);
  const from = String(fax?.fromName || '').toUpperCase().slice(0, 24);
  g.fillText(`TO: ${to || '—'}`, MARGIN_X, y);
  g.textAlign = 'right';
  g.fillText(`FROM: ${from || '—'}`, w - MARGIN_X, y);
  g.textAlign = 'left';
  y += 34;

  if (fax?.subject) {
    g.font = '400 24px ui-monospace, "SF Mono", Menlo, Consolas, monospace';
    g.fillText(`RE: ${String(fax.subject).toUpperCase().slice(0, 46)}`, MARGIN_X, y);
    y += 30;
  }
  if (fax?.memo) {
    g.font = '400 24px ui-monospace, "SF Mono", Menlo, Consolas, monospace';
    g.fillText(String(fax.memo).slice(0, 58), MARGIN_X, y);
    y += 34;
  }
  g.fillRect(MARGIN_X, h - 8, w - MARGIN_X * 2, 2);
  g.textBaseline = 'middle';
}

/**
 * The cover sheet, drawn as a fax machine would print it.
 *
 * Without this a memo fax is a blank page: the header and footer print, and
 * nothing else, because the only thing renderPage drew was the source image.
 * A cover sheet IS the content for one of the two things this app faxes.
 *
 * Set in mono at a size that survives the dither. Text is thresholded, never
 * error-diffused — dithering a black stroke only makes it fuzzy, and a real
 * machine thresholded it too.
 */
function drawCoverSheet(g, fax, w, h) {
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, w, h);
  g.fillStyle = '#000000';
  g.textBaseline = 'top';

  const pad = 56;
  let y = 40;
  const rule = () => {
    g.fillRect(pad, y, w - pad * 2, 2);
    y += 26;
  };

  g.font = '800 46px ui-monospace, "SF Mono", Menlo, Consolas, monospace';
  g.fillText('FACSIMILE', pad, y);
  y += 62;
  rule();

  const row = (label, value) => {
    g.font = '700 22px ui-monospace, "SF Mono", Menlo, Consolas, monospace';
    g.fillText(label, pad, y);
    g.font = '400 26px ui-monospace, "SF Mono", Menlo, Consolas, monospace';
    g.fillText(String(value || '—').toUpperCase().slice(0, 28), pad + 150, y - 2);
    y += 44;
  };
  row('TO', fax?.toName);
  row('FROM', fax?.fromName);
  row('SUBJECT', fax?.subject);
  row('PAGES', `${fax?.pages || 1}`);
  y += 8;
  rule();

  if (fax?.memo) {
    g.font = '400 28px ui-monospace, "SF Mono", Menlo, Consolas, monospace';
    // Wrap by hand: canvas has no text box.
    const words = String(fax.memo).split(/\s+/);
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (g.measureText(test).width > w - pad * 2) {
        g.fillText(line, pad, y); y += 38; line = word;
      } else line = test;
    }
    if (line) g.fillText(line, pad, y);
  }
  g.textBaseline = 'middle';
}


/**
 * Decode a stored source, or return null.
 *
 * Never throws. Demo fixtures put a plain string in `source`, and a real photo
 * can be a HEIC this browser cannot read — in both cases the honest answer is
 * "no image", which renders a cover sheet, rather than an EncodingError that
 * takes the screen down with it.
 */
export async function loadSource(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) return null;
  try {
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    return img;
  } catch {
    return null;
  }
}

/** Draw the source "contain" on white. A photo on a page is a photo on a page. */
function drawContained(g, src, x, y, w, h) {
  g.fillStyle = '#ffffff';
  g.fillRect(x, y, w, h);
  const sw = src.width || src.naturalWidth;
  const sh = src.height || src.naturalHeight;
  if (!sw || !sh) return false;
  // FIT IN OUTPUT SPACE, DRAW IN GRID SPACE.
  //
  // The grid is 1080x714 and the page it becomes is 1080x1428: every row is
  // printed twice, which is what gives a fax its squat geometry. Fitting the
  // source into the grid's own box and then stretching the page meant every
  // picture came out twice as tall as it was wide — a portrait video was a
  // column a third of the page across, and a face was a long face. So the fit
  // is done against the box the source will OCCUPY ON THE PAGE (w by h*V_SCALE)
  // and the result is drawn at half that height here, so that after the
  // stretch it is its own shape again.
  const scale = Math.min(w / sw, (h * V_SCALE) / sh);
  const dw = Math.round(sw * scale);
  const dh = Math.round((sh * scale) / V_SCALE);
  g.drawImage(src, x + ((w - dw) >> 1), y + ((h - dh) >> 1), dw, dh);
  return true;
}

/**
 * Render one page.
 *
 * @param {object} o
 * @param {*}      o.source     an ImageBitmap/HTMLImageElement, or null for a memo
 * @param {object} o.fax        the recipe row
 * @param {object} o.machine    tsi, number, dateFormat, toner
 * @param {string} o.replyUrl   printed in the footer — the only carrier that
 *                              survives a messenger recompressing the image
 * @param {(n:number)=>void} [o.onProgress] 0..1, real, for the honest third of
 *                              the transmission bar
 * @returns {Promise<{canvas: any, blank: boolean}>}
 */
export async function renderPage({
  source, fax, machine = {}, replyUrl = '', onProgress,
}) {
  const wreck = WRECK[fax?.condition] ?? WRECK.used;
  const rnd = mulberry32(fax?.seed ?? 1);

  // --- 1. the content area, at grid resolution, still greyscale -------------
  const { canvas: work, g: wg } = ctx2d(GRID_W, CONTENT_H);
  let hasImage = false;
  if (source) {
    // A photo fax still carries its cover information. Printing the picture
    // and silently dropping TO, SUBJECT and the memo made those fields on the
    // compose screen a lie — you typed them and they went nowhere.
    const band = coverBandHeight(fax);
    wg.fillStyle = '#ffffff';
    wg.fillRect(0, 0, GRID_W, CONTENT_H);
    if (band) drawCoverBand(wg, fax, GRID_W, band);
    hasImage = drawContained(wg, source, 0, band, GRID_W, CONTENT_H - band);
  } else {
    drawCoverSheet(wg, fax, GRID_W, CONTENT_H);
  }

  const img = wg.getImageData(0, 0, GRID_W, CONTENT_H);

  // Blank-canvas guard: iOS returns transparent black rather than erroring.
  let blank = true;
  for (let p = 3; p < img.data.length; p += 4000) {
    if (img.data[p] !== 0) { blank = false; break; }
  }
  if (source && blank) return { canvas: null, blank: true };
  blank = false;

  // --- 2. tone, sharpen, dither --------------------------------------------
  // MINT IGNORES THE TONER, and that is not a rounding error in the joke.
  // MINT is the legibility floor — it is what somebody switches to in order to
  // read the page at all — and every artefact function already no-ops at
  // wreck 0. The tone curve did not: an empty cartridge lifts the ink to 35% of
  // its density BEFORE the threshold, so at MINT on a well-used machine the
  // dither drops most of the dots out of the letterforms and the cover sheet
  // comes out as pale confetti. The floor has to be a floor from every
  // direction. The cartridge still runs down, and USED and HAUNTED still show
  // it, which is where the joke lives anyway.
  const toner = wreck > 0 ? (machine.toner ?? 100) / 100 : 1;
  const lut = buildLut({ toner });
  let grey = toGrey(img.data, GRID_W, CONTENT_H, lut);
  if (hasImage && fax?.kind !== 'memo') grey = unsharp(grey, GRID_W, CONTENT_H, 0.4);
  tonerBanding(grey, GRID_W, CONTENT_H, wreck, rnd);
  onProgress?.(0.33);

  const algo = algorithmFor(fax?.kind || 'photo', !!fax?.fine);
  const bits = toBytes(algo(toWorkBuffer(grey), GRID_W, CONTENT_H));
  onProgress?.(0.66);

  // Back into an ImageData so it can be composited onto the page.
  for (let i = 0, p = 0; i < bits.length; i += 1, p += 4) {
    img.data[p] = img.data[p + 1] = img.data[p + 2] = bits[i];
    img.data[p + 3] = 255;
  }
  wg.putImageData(img, 0, 0);

  // --- 3. compose the page --------------------------------------------------
  const { canvas: page, g } = ctx2d(GRID_W, GRID_H);
  g.fillStyle = PAPER;
  g.fillRect(0, 0, GRID_W, GRID_H);

  // Skew before the content lands: paper feeds crooked. MINT never skews.
  if (wreck > 0) {
    g.save();
    g.translate(GRID_W / 2, GRID_H / 2);
    g.rotate(((rnd() * 2 - 1) * 0.4 * wreck * Math.PI) / 180);
    g.translate(-GRID_W / 2, -GRID_H / 2);
  }
  g.drawImage(work, 0, CONTENT_TOP);
  if (wreck > 0) g.restore();

  // The printed bands go on last and are never skewed — a streak through the
  // reply URL is a dead reply loop.
  g.fillStyle = INK;
  g.font = `600 15px ui-monospace, "SF Mono", Menlo, Consolas, monospace`;
  g.textBaseline = 'middle';
  const hl = headerLine(fax, machine);
  g.fillText(hl.left, MARGIN_X, HEADER_TOP + HEADER_H / 2);
  g.fillText(hl.mid, MARGIN_X + 120, HEADER_TOP + HEADER_H / 2);
  g.textAlign = 'right';
  g.fillText(hl.page, GRID_W - MARGIN_X, HEADER_TOP + HEADER_H / 2);
  g.textAlign = 'left';
  if (hl.num) g.fillText(hl.num, GRID_W - MARGIN_X - 260, HEADER_TOP + HEADER_H / 2);
  g.fillRect(MARGIN_X, HEADER_TOP + HEADER_H + 2, GRID_W - MARGIN_X * 2, 1);

  if (replyUrl) {
    g.font = `600 14px ui-monospace, "SF Mono", Menlo, Consolas, monospace`;
    // Printed as given, not uppercased: the query string (?re=NAME) is read
    // case-sensitively by the page it points at.
    g.fillText(`REPLY BY FAX: ${replyUrl}`, MARGIN_X, GRID_H - MARGIN_Y - FOOTER_H / 2);
  }

  // --- 4. artefacts, then the one threshold that guarantees the invariant ---
  const pageImg = g.getImageData(0, 0, GRID_W, GRID_H);
  const flat = new Uint8ClampedArray(GRID_W * GRID_H);
  for (let i = 0, p = 0; i < flat.length; i += 1, p += 4) {
    flat[i] = pageImg.data[p] < MID ? 0 : 255;   // hard threshold, kills antialiasing
  }
  applyPost(flat, GRID_W, GRID_H, wreck, rnd);
  for (let i = 0, p = 0; i < flat.length; i += 1, p += 4) {
    pageImg.data[p] = pageImg.data[p + 1] = pageImg.data[p + 2] = flat[i];
    pageImg.data[p + 3] = 255;
  }
  g.putImageData(pageImg, 0, 0);
  onProgress?.(1);

  // --- 5. print it: double every scanline, with smoothing OFF ---------------
  const { canvas: out, g: og } = ctx2d(OUT_W, OUT_H);
  og.imageSmoothingEnabled = false;
  og.drawImage(page, 0, 0, OUT_W, OUT_H);

  return { canvas: out, blank: false };
}

/**
 * A small copy of the finished page, as a data URL.
 *
 * WHY THE APP NEEDS ONE. Every fax row has always had a `thumb` field and
 * nothing has ever written to it, so the out tray on the front of the machine
 * — the part whose whole job is to hold the last page it printed — showed
 * "OUTBOX EMPTY. NOTHING HAS LEFT THIS BUILDING." above a counter reading 1,
 * and every row in the outbox was a line of text. The machine is the
 * navigation; an empty out tray is the machine lying about itself.
 *
 * SMOOTHING IS ON HERE, DELIBERATELY, and it is the opposite of the rule
 * everywhere else in this file. A 1-bit dither resampled with nearest
 * neighbour beats against the new pixel grid and comes out as moiré; averaged
 * down it comes out as grey, which at 56px in a list is exactly what a fax
 * looks like from across a desk. The shared artefact is still the full-size
 * PNG — this is never handed to anybody.
 */
export function thumbnail(canvas, width = 540) {
  try {
    const height = Math.round((canvas.height / canvas.width) * width);
    const c = Object.assign(document.createElement('canvas'), { width, height });
    const g = c.getContext('2d');
    g.fillStyle = PAPER;
    g.fillRect(0, 0, width, height);
    g.drawImage(canvas, 0, 0, width, height);
    // JPEG, because this one is measured in kilobytes of IndexedDB rather
    // than in fidelity, and a page of grey compresses to nothing.
    const url = c.toDataURL('image/jpeg', 0.82);
    c.width = c.height = 0;
    return url;
  } catch {
    // A thumbnail is a nicety. Never let it take the transmission with it.
    return '';
  }
}
